import AppModel from 'Models/AppModel';
import {
  getArticleJsonLd,
  getProductJsonLd,
  getAuthorJsonLd,
} from 'Root/helpers/seo';

const SEO_META_TAGS = [
  { name: 'description', content: 'content' },
  { name: 'og:type', content: 'content' },
  { name: 'og:title', content: 'content' },
  { name: 'og:url', content: 'content' },
  { name: 'og:image', content: 'content' },
  { name: 'og:description', content: 'content' },
];

const ENTITIES = [
  { entity: 'product', jsonLdGenerator: getProductJsonLd },
  { entity: 'article', jsonLdGenerator: getArticleJsonLd },
  { entity: 'author', jsonLdGenerator: getAuthorJsonLd },
];

function getSeoObject(vm) {
  const { seo } = vm.$options;

  if (!seo) {
    return null;
  }

  return typeof seo === 'function' ? seo.call(vm) : seo;
}

function getMetaTagByName(name) {
  return document.head.querySelector(`meta[name="${name}"]`);
}

function handleClientSeo(vm) {
  const seo = getSeoObject(vm);

  if (!seo) {
    return;
  }

  const { title, meta } = seo;

  document.title = title || AppModel.APP_TITLE;

  SEO_META_TAGS.forEach((metaTag) => {
    let metaNode = getMetaTagByName(metaTag.name);
    const newContentValue = meta ? meta[metaTag.name] : null;

    if (newContentValue) {
      if (!metaNode) {
        metaNode = document.createElement('meta');
        metaNode.setAttribute('name', metaTag.name);
        document.head.appendChild(metaNode);
      }

      const currentContentValue = metaNode.getAttribute(metaTag.content);

      if (currentContentValue !== newContentValue) {
        metaNode.setAttribute(metaTag.content, newContentValue);
      }
    } else if (metaNode) {
      metaNode.remove();
    }
  });

  if (seo.canonical) {
    const linkCanonical = document.querySelector('link[rel="canonical"]');
    linkCanonical.setAttribute('href', seo.canonical);
  }

  ENTITIES.forEach(({ entity, jsonLdGenerator }) => {
    let scriptNode = document.querySelector(`#jsonld-${entity}`);

    if (seo[entity]) {
      const jsonLd = jsonLdGenerator(seo[entity]);

      if (!scriptNode) {
        scriptNode = document.createElement('script');
        scriptNode.setAttribute('id', `jsonld-${entity}`);
        scriptNode.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptNode);
      }
      scriptNode.textContent = JSON.stringify(jsonLd);
      return;
    }

    if (scriptNode) {
      scriptNode.remove();
    }
  });
}

const serverMixin = {
  created() {
    const seo = getSeoObject(this);

    if (!seo) {
      return;
    }

    const { title, meta } = seo;

    if (title) {
      this.$ssrContext.title = title;
    }

    if (meta) {
      let metaHtml = '';

      Object.keys(meta).forEach((metaTag) => {
        if (meta[metaTag]) {
          metaHtml += `<meta name="${metaTag}" content="${meta[metaTag]}">\n`;
        }
      });

      this.$ssrContext.meta += `\n${metaHtml}`;
    }

    ENTITIES.forEach(({ entity, jsonLdGenerator }) => {
      if (!seo[entity]) {
        return;
      }

      const scriptId = `jsonld-${entity}`;
      const jsonLd = JSON.stringify(jsonLdGenerator(seo[entity]));

      this.$ssrContext.meta += `\n<script id="${scriptId}" type="application/ld+json">\n${jsonLd}\n</script>`;
    });

    if (seo.canonical) {
      this.$ssrContext.canonicalAbsolute = seo.canonical;
    }

    if (seo.relNext) {
      this.$ssrContext.meta += `\n<link rel="next" href="${seo.relNext}">`;
    }

    if (seo.relPrev) {
      this.$ssrContext.meta += `\n<link rel="prev" href="${seo.relPrev}">`;
    }
  },
};

const clientMixin = {
  mounted() {
    handleClientSeo(this, 'mounted');
  },

  // sometimes event firing together with mounted
  updated() {
    handleClientSeo(this, 'updated');
  },
};

export default process.env.VUE_ENV === 'server'
  ? serverMixin
  : clientMixin;
